'use client';
import { shopVar } from "@/lib/shopStyles";
import type { Product } from "@/lib/TicketApi";
import { Money } from "@/lib/money";
import ProductAvailability from "./ProductAvailability";
import { useLanguage } from '@/providers/LanguageProvider';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import DonationProduct from './DonationProduct';
import GroupedProduct from "./GroupedProduct";
import CashcardProduct from './CashcardProduct';
import { Fees } from '@/components/shared/Fees';

interface ProductSectionProps {
    product: Product;
}

export function Product({product}: ProductSectionProps) {
    const { currentLanguage: lang } = useLanguage();
    const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
    const hasDescription = product.description && product.description.trim() !== '';

    if (product.type === 'grouped_product') {
        return <GroupedProduct product={product} />;
    }
    if (product.type === 'donation') {
        return <DonationProduct product={product} />;
    }
    if (product.type === 'cashcard') {
        return <CashcardProduct product={product} />;
    }

    // Only handle standard products here
    return (
        <div
            className="border rounded-md px-4 pt-4 pb-2 mb-4 shadow-md last:mb-0 overflow-hidden"
            style={{ borderColor: shopVar('--shop-borderColor') }}>
            <div className="flex items-start border-b border-dashed w-full pb-4" style={{ borderColor: shopVar('--shop-borderColor') }}>
                <div className="flex-grow pr-2">
                    <div className="flex items-center">
                        <h2 className="text-base font-bold mr-2" style={{ color: shopVar('--shop-textColor') }}>{product.name}</h2>
                        {hasDescription && (
                            <button
                                onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                                aria-expanded={isDescriptionOpen}
                                aria-controls={`product-description-${product.productId}`}
                                className="flex-shrink-0 text-[var(--shop-textColor)] hover:opacity-70 transition-opacity mr-1"
                            >
                                <InfoCircledIcon className="w-4 h-4"/>
                            </button>
                        )}
                    </div>
                    <p className="font-normal text-xs tracking-tight mt-1" style={{ color: shopVar('--shop-textColor') }}>
                        {Money.fromInteger(product.price.amount, product.price.currency).display(lang)}
                        <Fees serviceFees={product.serviceFees} locale={lang} />
                    </p>
                </div>
            </div>
            <AnimatePresence initial={false}>
                {hasDescription && isDescriptionOpen && (
                    <motion.div
                        id={`product-description-${product.productId}`}
                        key="description-content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { height: "auto", opacity: 1, paddingTop: '0.5rem', paddingBottom: '0.5rem' },
                            collapsed: { height: 0, opacity: 0, paddingTop: 0, paddingBottom: 0 }
                        }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="text-sm border-b border-dashed overflow-hidden"
                        style={{ borderColor: shopVar('--shop-borderColor') }}
                    >
                        <p className="py-1" style={{ color: shopVar('--shop-textColor') }}>{product.description}</p>
                    </motion.div>
                )}
            </AnimatePresence>
            <div className="pt-2">
                <ProductAvailability product={product}/>
            </div>
        </div>
    );
}
